import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  // MenuItem, // only used by hidden "By journey" UI
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { memberListing } from "src/DAL/member/Member1";
import { _get_user_from_localStorage } from "src/DAL/localstorage/LocalStorage";
import { ErrorText, inputSx, MUTED } from "./builderUi";

/** Coach portal: same shape as admin, scoped to this coach's members/journeys. */
export const INDIVIDUAL_AUDIENCE_TYPES = [
  { value: "all", label: "All my members" },
  // Hidden: Individual member automations should not use journey audience.
  // { value: "tracker", label: "By journey" },
  { value: "members", label: "Specific members" },
];

// Hidden with "By journey" audience option.
// const TRACKER_STATUS_OPTIONS = [
//   { value: "active", label: "Active in journey" },
//   { value: "left", label: "Left the journey" },
//   { value: "all", label: "Everyone who joined" },
// ];

const MEMBER_SEARCH_LIMIT = 20;
const MEMBER_SELECT_MAX = 500;
const SEARCH_DEBOUNCE_MS = 400;

const memberName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
  member?.email ||
  "Unnamed member";

const currentCoachId = () => {
  try {
    const user = _get_user_from_localStorage() || {};
    return String(user._id || user.id || "");
  } catch (_e) {
    return "";
  }
};

export const personFromMemberUser = (member) => {
  if (!member) return null;
  return {
    personKey: "",
    memberId: String(member._id || member.id || ""),
    email: String(member.email || "").trim().toLowerCase(),
    name: memberName(member),
    first_name: member.first_name || "",
    last_name: member.last_name || "",
    _id: member._id || member.id,
  };
};

export const INITIAL_INDIVIDUAL_AUDIENCE = {
  audienceType: "members",
  members: [],
  trackerStatus: "active",
};

/**
 * Build scopeMember for the API. Coach "all" stores coachId so the runner
 * only matches people assigned to this coach.
 */
export const buildIndividualScopeMember = (audience) => {
  const type = audience?.audienceType || "members";
  const coachId = currentCoachId();
  if (type === "all") {
    return {
      audienceType: "all",
      members: [],
      trackerStatus: "active",
      coachId,
    };
  }
  if (type === "tracker") {
    return {
      audienceType: "tracker",
      members: [],
      trackerStatus: audience.trackerStatus || "active",
      coachId,
    };
  }
  const members = (Array.isArray(audience?.members) ? audience.members : [])
    .map((m) => ({
      personKey: String(m.personKey || ""),
      memberId: String(m.memberId || m._id || ""),
      email: String(m.email || "").trim().toLowerCase(),
      name: m.name || memberName(m),
    }))
    .filter((m) => m.memberId || m.email || m.personKey);
  if (!members.length) return null;
  const first = members[0];
  return {
    audienceType: "members",
    members,
    trackerStatus: "active",
    coachId,
    personKey: first.personKey,
    memberId: first.memberId,
    email: first.email,
    name: members.length === 1 ? first.name : `${members.length} members`,
  };
};

export const audienceFromScopeMember = (scopeMember) => {
  if (!scopeMember || typeof scopeMember !== "object") {
    return { ...INITIAL_INDIVIDUAL_AUDIENCE };
  }
  const type = String(scopeMember.audienceType || "").toLowerCase();
  if (type === "all") {
    return { audienceType: "all", members: [], trackerStatus: "active" };
  }
  if (type === "tracker") {
    return {
      audienceType: "tracker",
      members: [],
      trackerStatus: scopeMember.trackerStatus || "active",
    };
  }
  const list = Array.isArray(scopeMember.members)
    ? scopeMember.members
    : scopeMember.memberId || scopeMember.email || scopeMember.personKey
      ? [scopeMember]
      : [];
  return {
    audienceType: "members",
    members: list.map((m) => ({
      ...m,
      _id: m.memberId || m._id,
      name: m.name || memberName(m),
    })),
    trackerStatus: "active",
  };
};

function MemberPicker({ value, onChange }) {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    const timer = setTimeout(async () => {
      // Coach member list (same API the Members page uses).
      const result = await memberListing(0, MEMBER_SEARCH_LIMIT, {
        search_text: String(input || "").trim(),
        program: [],
        status: "",
        filter: "",
      });
      if (requestRef.current !== requestId) return;
      setLoading(false);
      if (result.code === 200) {
        setOptions(
          (result.member || [])
            .map((m) => personFromMemberUser(m))
            .filter(Boolean)
        );
        setError("");
      } else {
        setOptions([]);
        setError(result.message || "Couldn't search members");
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const atLimit = value.length >= MEMBER_SELECT_MAX;
  const personId = (p) => p?.memberId || p?._id || p?.email || "";

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      options={options}
      value={value}
      loading={loading}
      filterOptions={(items) => items}
      getOptionLabel={(p) =>
        p?.email && p.email !== p.name
          ? `${p.name} (${p.email})`
          : p?.name || ""
      }
      isOptionEqualToValue={(option, selected) =>
        personId(option) === personId(selected)
      }
      getOptionDisabled={() => atLimit}
      onChange={(_e, selected) => onChange(selected)}
      onInputChange={(_e, text, reason) => {
        if (reason === "input" || reason === "clear") setInput(text);
      }}
      noOptionsText={error || "No members found"}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Members"
          placeholder="Search your members…"
          helperText={
            atLimit
              ? `You can select up to ${MEMBER_SELECT_MAX} members`
              : `${value.length} selected`
          }
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default function IndividualAudiencePicker({
  audience,
  onChange,
  trackerId,
  trackerOptions = [],
  trackerName = "",
  onTrackerChange,
  errors = {},
}) {
  const update = (patch) => onChange({ ...audience, ...patch });

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: MUTED,
          textTransform: "uppercase",
          letterSpacing: 0.35,
          mb: 0.5,
        }}
      >
        Who
      </Typography>
      <RadioGroup
        row
        value={
          audience.audienceType === "all" || audience.audienceType === "members"
            ? audience.audienceType
            : "members"
        }
        onChange={(e) => update({ audienceType: e.target.value })}
        sx={{ mb: 1.25 }}
      >
        {INDIVIDUAL_AUDIENCE_TYPES.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={option.label}
          />
        ))}
      </RadioGroup>

      {audience.audienceType === "all" ? (
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          Emails go to your members (any of your journeys).
        </Typography>
      ) : null}

      {/* Hidden with "By journey" audience option above.
      {audience.audienceType === "tracker" ? (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Journey"
            required
            value={trackerId || ""}
            onChange={(e) => onTrackerChange?.(e.target.value)}
            error={Boolean(errors.trackerId)}
            helperText={errors.trackerId || "Only journeys assigned to you"}
            sx={{ ...inputSx, flex: "2 1 280px" }}
          >
            <MenuItem value="">Select journey…</MenuItem>
            {trackerId &&
            !trackerOptions.some((t) => t.id === trackerId) ? (
              <MenuItem value={trackerId}>{trackerName || "—"}</MenuItem>
            ) : null}
            {trackerOptions.map((tracker) => (
              <MenuItem key={tracker.id} value={tracker.id}>
                {tracker.name || "Untitled"}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="People"
            value={audience.trackerStatus || "active"}
            onChange={(e) => update({ trackerStatus: e.target.value })}
            sx={{ ...inputSx, flex: "1 1 200px" }}
          >
            {TRACKER_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      ) : null}
      */}

      {audience.audienceType === "members" ? (
        <Box>
          <MemberPicker
            value={audience.members || []}
            onChange={(members) => update({ members })}
          />
          {errors.scopeMember ? (
            <ErrorText sx={{ mt: 0.75 }}>{errors.scopeMember}</ErrorText>
          ) : (
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
              Only members on your list. Emails go to these people.
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
